<!--
  BannerManagement.vue
  배너 관리 페이지
  
  주요 기능:
  - 배너 목록 조회 및 관리
  - 배너 추가/수정/삭제
  - 배너 노출 여부 관리
  - 배너 클릭 통계 조회
  
  API 엔드포인트:
  - GET /banners - 배너 목록 조회
  - POST /banners - 배너 추가
  - PUT /banners/:id - 배너 수정
  - DELETE /banners/:id - 배너 삭제
-->
<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div style="margin-top: 28px"></div>
    
    <!-- 페이지 헤더 영역 -->
    <div class="card-header pb-0 col-sm-12 d-flex">
      <div class="col-sm-12 col-xs-12">
        <div class="col-sm-12 col-xs-12 justify-content-between titleContainer">
          <div class="title-row">
            <h1 class="fw-bold page-title">
              배너 관리
            </h1>
            <div class="header-actions">
              <a-button 
                type="default"
                :loading="state.tableLoading"
                @click="handleRefresh"
                class="refresh-button"
              >
                <i class="fa-solid fa-refresh"></i>
                조회
              </a-button>
            </div>
          </div>
          <span class="pageDesc">웹사이트 배너를 관리합니다.</span>
        </div>
      </div>
    </div>
    
    <!-- 콘텐츠 영역 -->
    <div class="px-0 pb-2">
      <!-- 배너 관리 영역 -->
      <div class="card content-wrap-card">
        <!-- 배너 테이블 컴포넌트 -->
        <BannerTable
          v-model:bannerList="bannerList"
          :loading="state.tableLoading"
          :pagination="pagination"
          @add="handleAdd"
          @edit="handleEdit"
          @delete="handleDelete"
          @toggle-display="handleToggleDisplay"
          @table-change="handleTableChange"
          @drag-end="handleDragEnd"
        />
      </div>
    </div>
    
    <!-- 배너 추가/수정 모달 컴포넌트 -->
    <BannerModal
      :open="state.modalVisible"
      :loading="state.modalLoading"
      :is-edit="state.isEdit"
      :banner-data="currentBannerData"
      @ok="handleModalOk"
      @cancel="handleModalCancel"
    />
  </div>
</template>

<script>
import { ref, reactive, onMounted, getCurrentInstance } from 'vue';
import { message } from 'ant-design-vue';
import { twoBtnDialog, formatCommaNumber, convertToLocalTime } from '@/utils/utils';
import BannerTable from './components/BannerTable.vue';
import BannerModal from './components/BannerModal.vue';

export default {
  name: "BannerManagement",
  components: {
    BannerTable,
    BannerModal
  },
  setup() {
    // API 인스턴스 초기화
    const instance = getCurrentInstance();
    let apiInstance = null;
    
    if (instance && instance.proxy) {
      apiInstance = instance.proxy.$apiInstance;
    } else {
      console.error('Current instance not available, API initialization failed');
    }

    // 반응형 상태 정의
    const state = reactive({
      tableLoading: false,
      modalVisible: false,
      modalLoading: false,
      isEdit: false,
      editingId: null
    });

    // 데이터 상태
    const bannerList = ref([]);
    const currentBannerData = ref({});
    
    // 페이지네이션
    const pagination = reactive({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total, range) => `${range[0]}-${range[1]} / 총 ${total}개`
    });
    
    // 공통 에러 처리 함수
    const handleError = (error, defaultMessage) => {
      console.error('Error occurred:', error);
      
      let errorMessage = defaultMessage;
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
      return errorMessage;
    };

    // API 인스턴스 체크 함수
    const checkApiInstance = () => {
      if (!apiInstance) {
        message.error('API가 초기화되지 않았습니다.');
        return false;
      }
      return true;
    };

    // 배너 목록 로드
    const loadBanners = async () => {
      if (!checkApiInstance()) return;
      
      state.tableLoading = true;
      
      try {
        const params = {
          page: pagination.current,
          limit: pagination.pageSize
        };
        
        const response = await apiInstance.get('/banners', { params });
        
        const { data } = response;
        if (!data) {
          bannerList.value = [];
          pagination.total = 0;
          return;
        }

        // 데이터 매핑 함수
        const mapBannerData = (items) => items.map((item, index) => ({
          ...item,
          index: (pagination.current - 1) * pagination.pageSize + index + 1,
          id: item._id || item.id
        }));

        // 후단 API 응답 구조에 맞춰 데이터 처리
        if (data.docs && Array.isArray(data.docs)) {
          // docs 배열 사용 (주요 구조)
          bannerList.value = mapBannerData(data.docs);
          pagination.total = data.totalDocs || 0;
          pagination.current = data.page || 1;
          pagination.pageSize = data.limit || 10;
        } else if (data.data && Array.isArray(data.data)) {
          // 기존 data 배열 구조 지원 (호환성)
          bannerList.value = mapBannerData(data.data);
          pagination.total = data.pagination?.total || data.total || 0;
          pagination.current = data.pagination?.page || data.page || 1;
        } else if (Array.isArray(data)) {
          // 단순 배열 구조 지원 (호환성)
          bannerList.value = mapBannerData(data);
          pagination.total = data.length;
        } else {
          bannerList.value = [];
          pagination.total = 0;
        }
      } catch (error) {
        handleError(error, '배너 목록을 불러오는 중 오류가 발생했습니다.');
        // 에러 시 데이터 초기화
        Object.assign(pagination, { total: 0 });
        bannerList.value = [];
      } finally {
        state.tableLoading = false;
      }
    };
    
    // 기본 배너 데이터
    const getDefaultBannerData = () => ({
      bannerName: '',
      linkUrl: '',
      bannerLink: '',
      displayStartDate: null,
      displayEndDate: null,
      isVisible: 'Y',
      order: 1,
      description: ''
    });

    // 배너 추가
    const handleAdd = () => {
      Object.assign(state, { isEdit: false, editingId: null, modalVisible: true });
      currentBannerData.value = getDefaultBannerData();
    };
    
    // 배너 수정
    const handleEdit = (record) => {
      state.isEdit = true;
      state.editingId = record._id || record.id;
      currentBannerData.value = { ...record };
      state.modalVisible = true;
    };
    
    // 배너 삭제
    const handleDelete = async (id) => {
      if (!checkApiInstance()) return;
      
      const isConfirmed = await twoBtnDialog(
        '배너 삭제',
        '정말로 이 배너를 삭제하시겠습니까?',
        '삭제'
      );
      
      if (!isConfirmed) return;
      
      try {
        await apiInstance.delete(`/banners/${id}`);
        message.success('배너가 성공적으로 삭제되었습니다.');
        loadBanners();
      } catch (error) {
        handleError(error, '배너 삭제 중 오류가 발생했습니다.');
      }
    };
    
    // 배너 노출 여부 토글
    const handleToggleDisplay = async (id, currentVisibility) => {
      if (!checkApiInstance()) return;
      
      try {
        // 후단 API 스펙에 맞춤: /banners/:id/toggle-visibility
        const response = await apiInstance.patch(`/banners/${id}/toggle-visibility`);
        
        // 응답에서 새로운 상태 확인
        const newVisibility = response.data?.isVisible || (currentVisibility === 'Y' ? 'N' : 'Y');
        const displayText = newVisibility === 'Y' ? '노출' : '비노출';
        
        message.success(`배너가 성공적으로 ${displayText}로 변경되었습니다.`);
        loadBanners();
      } catch (error) {
        handleError(error, '배너 노출 상태 변경 중 오류가 발생했습니다.');
      }
    };
    
    // 모달 확인 처리
    const handleModalOk = async (formData) => {
      if (!checkApiInstance()) return;
      
      state.modalLoading = true;
      
      try {
        if (state.isEdit) {
          await apiInstance.put(`/banners/${state.editingId}`, formData);
          message.success('배너가 성공적으로 수정되었습니다.');
        } else {
          await apiInstance.post('/banners', formData);
          message.success('배너가 성공적으로 추가되었습니다.');
        }
        
        state.modalVisible = false;
        loadBanners();
      } catch (error) {
        handleError(error, state.isEdit ? '배너 수정 중 오류가 발생했습니다.' : '배너 추가 중 오류가 발생했습니다.');
      } finally {
        state.modalLoading = false;
      }
    };
    
    // 모달 취소 처리
    const handleModalCancel = () => {
      state.modalVisible = false;
      currentBannerData.value = {};
    };
    
    // 테이블 변경 핸들러
    const handleTableChange = (pag, filters, sorter) => {
      if (pagination.current !== pag.current || pagination.pageSize !== pag.pageSize) {
        pagination.current = pag.current;
        pagination.pageSize = pag.pageSize;
        loadBanners();
      }
    };
    
    // 조회 버튼 클릭 핸들러
    const handleRefresh = () => {
      loadBanners();
      message.success('배너 목록을 새로고침했습니다.');
    };

    // 드래그 앤 드롭 순서 변경 핸들러
    const handleDragEnd = async (dragEvent) => {
      if (!checkApiInstance()) return;
      
      const { newIndex, oldIndex, item } = dragEvent;
      
      // 같은 위치로 드래그한 경우 무시
      if (newIndex === oldIndex) return;
      
      try {
        // 순서 변경 API 호출
        const response = await apiInstance.patch(`/banners/${item._id}/reorder`, {
          newOrder: newIndex + 1, // 1부터 시작하는 순서
          oldOrder: oldIndex + 1
        });
        
        message.success('배너 순서가 성공적으로 변경되었습니다.');
        
        // 순서 변경 후 목록 새로고침
        loadBanners();
      } catch (error) {
        // 에러 발생 시 원래 순서로 되돌리기
        const originalList = [...bannerList.value];
        const movedItem = originalList.splice(newIndex, 1)[0];
        originalList.splice(oldIndex, 0, movedItem);
        bannerList.value = originalList;
        
        handleError(error, '배너 순서 변경 중 오류가 발생했습니다.');
      }
    };

    // 컴포넌트 마운트 시 데이터 로드
    onMounted(() => {
      loadBanners();
    });
    
    return {
      // 상태관리
      state,
      pagination,
      
      // 데이터
      bannerList,
      currentBannerData,
      
      // 이벤트 핸들러
      handleAdd,
      handleEdit,
      handleDelete,
      handleToggleDisplay,
      handleModalOk,
      handleModalCancel,
      handleTableChange,
      handleRefresh,
      handleDragEnd,
      
      // 유틸 함수
      formatCommaNumber,
      convertToLocalTime
    };
  }
};
</script>

<style scoped lang="scss">
.container-wrap {
  padding: 0 16px;
}



.titleContainer {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.page-title {
  color: #000;
  font-size: 1.8rem;
  margin-bottom: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.refresh-button {
  height: 36px;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  background: #fff;
  color: #333;
  
  &:hover {
    border-color: #FFCC32;
    color: #FFCC32;
  }
  
  &:focus {
    border-color: #FFCC32;
    box-shadow: 0 0 0 2px rgba(255, 204, 50, 0.2);
  }
  
  i {
    margin-right: 6px;
  }
}

.pageDesc {
  color: #6c757d;
  font-size: 14px;
  margin-top: 8px;
}
</style>
